import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";

/**
 * PermissionsLoader - Full-screen loading component shown during root-level user permission bootstrap
 *
 * NOTE: This component renders BEFORE the main app layout and cannot rely on Bootstrap CSS
 * or app stylesheets being fully loaded. All styles are inlined to ensure correct display
 * during the critical bootstrap phase, regardless of CSS loading state.
 *
 * Uses Bootstrap CSS variables (--bs-body-bg, --bs-body-color) for theme-awareness,
 * but all layout and animation styles are self-contained.
 *
 * After 8 seconds, shows a "Taking longer than expected" message with a logout link
 * to prevent users from being stuck behind the loader indefinitely.
 */
export const PermissionsLoader: React.FC = () => {
  const { instance } = useMsal();
  const [showEscapeHatch, setShowEscapeHatch] = useState(false);

  // Show escape hatch after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowEscapeHatch(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // Handle logout - clear MSAL cache and redirect to home
  const handleLogout = () => {
    instance.setActiveAccount(null);
    instance.clearCache();
    window.location.href = "/";
  };

  return (
    <div className="d-flex align-items-center justify-content-center min-vh-100 permissions-loader-bg">
      <style>{`
        .permissions-loader-bg {
          background-color: var(--bs-body-bg);
          color: var(--bs-body-color);
          animation: permissions-fade-out 0.35s ease-out forwards;
          backdrop-filter: blur(10px);
        }
        @keyframes permissions-fade-out {
          from {
            opacity: 1;
            backdrop-filter: blur(10px);
          }
        }
        .permissions-loader-dots {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        .permissions-loader-dot {
          width: 0.75rem;
          height: 0.75rem;
          border-radius: 50%;
          background-color: var(--bs-body-color);
          animation: permissions-loader-pulse 1.4s infinite;
        }
        .permissions-loader-dot:nth-child(1) {
          animation-delay: 0s;
        }
        .permissions-loader-dot:nth-child(2) {
          animation-delay: 0.2s;
        }
        .permissions-loader-dot:nth-child(3) {
          animation-delay: 0.4s;
        }
        
        .permissions-loading-text {
          position: relative;
        }
        .permissions-animated-dots {
          position: absolute;
        }
        .permissions-animated-dots::after {
          content: '...';
          animation: permissions-dots-animation 1.5s steps(4, end) infinite;
          display: inline-block;
          width: 1.5em;
          text-align: left;
        }
        @keyframes permissions-dots-animation {
          0% { content: ''; }
          25% { content: '.'; }
          50% { content: '..'; }
          75% { content: '...'; }
        }
        .permissions-escape-hatch {
          opacity: 0;
          animation: permissions-fade-in 0.5s ease-out forwards;
        }
        @keyframes permissions-fade-in {
          to { opacity: 1; }
        }
        .permissions-escape-link {
          color: var(--bs-primary, #0d6efd);
          text-decoration: underline;
          cursor: pointer;
          background: none;
          border: none;
          font-size: inherit;
          padding: 0;
        }
        .permissions-escape-link:hover {
          color: var(--bs-primary, #0d6efd);
          text-decoration: none;
        }
      `}</style>
      <div className="text-center">
        <div className="permissions-loader-dots mb-3">
          <div className="permissions-loader-dot"></div>
          <div className="permissions-loader-dot"></div>
          <div className="permissions-loader-dot"></div>
        </div>
        <h1>
          <span className="permissions-loading-text">Loading</span>
          {!showEscapeHatch && (
            <span className="permissions-animated-dots"></span>
          )}
        </h1>
        {showEscapeHatch && (
          <div className="permissions-escape-hatch mt-3">
            <p style={{ fontSize: "0.9rem", opacity: 0.7 }}>
              Taking longer than expected?
            </p>
            <button className="permissions-escape-link" onClick={handleLogout}>
              Re-authenticate
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
